'use client';

import Link, { type PrefetchLinkProps } from '@eliadmin/core/src/components/link';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { createHeaderPrefetchIntent } from '@/lib/header-prefetch-intent';

const IntentContext = createContext<ReturnType<typeof createHeaderPrefetchIntent> | null>(null);

export function CategoryIntentProvider({
  children,
  active,
}: {
  children: React.ReactNode;
  active: boolean;
}) {
  const router = useRouter();
  const intent = useMemo(
    () => createHeaderPrefetchIntent({ prefetch: href => router.prefetch(href) }),
    [router],
  );
  useEffect(() => {
    if (!active) intent.cancel();
  }, [active, intent]);
  useEffect(() => () => intent.cancel(), [intent]);
  return <IntentContext.Provider value={intent}>{children}</IntentContext.Provider>;
}

export function CategoryIntentLink({
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onClick,
  ...props
}: PrefetchLinkProps) {
  const intent = useContext(IntentContext);
  const target = (element: HTMLAnchorElement) => {
    const url = new URL(element.href, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}` : null;
  };
  return (
    <Link
      {...props}
      prefetch={false}
      prefetchOnHover={false}
      onMouseEnter={event => {
        const href = target(event.currentTarget);
        if (href) intent?.request(href);
        onMouseEnter?.(event);
      }}
      onMouseLeave={event => {
        const href = target(event.currentTarget);
        if (href) intent?.cancel(href);
        onMouseLeave?.(event);
      }}
      onFocus={event => {
        // Touch focus opens a submenu; it is not a request to render the parent page.
        if (event.currentTarget.matches(':focus-visible')) {
          const href = target(event.currentTarget);
          if (href) intent?.request(href);
        }
        onFocus?.(event);
      }}
      onBlur={event => {
        const href = target(event.currentTarget);
        if (href) intent?.cancel(href);
        onBlur?.(event);
      }}
      onClick={event => {
        intent?.cancel();
        onClick?.(event);
      }}
    />
  );
}
